import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { X, Archive, ArchiveRestore, Trash2, RotateCcw, Truck, FileText } from "lucide-react";

const STATUS_LABELS = {
  paid: "Payée",
  processing: "En préparation",
  shipped: "Expédiée",
  delivered: "Livrée",
  cancelled: "Annulée",
};

export default function OrderDetailModal({ order, onClose, onChanged }) {
  const [status, setStatus] = useState(order.status || "paid");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [carrier, setCarrier] = useState(order.carrier || "");
  const [notes, setNotes] = useState(order.notes || "");
  const [saving, setSaving] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [creatingShipment, setCreatingShipment] = useState(false);
  const [fetchingLabel, setFetchingLabel] = useState(false);

  const save = async () => {
    if (status === "shipped" && (!tracking.trim() || !carrier.trim())) {
      toast.error("Transporteur et numéro de suivi sont obligatoires pour expédier");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/shop/orders/${order.order_id}`, {
        status: status !== order.status ? status : undefined,
        tracking_number: tracking,
        carrier,
        notes,
      });
      toast.success("Commande mise à jour");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const toggleArchive = async () => {
    try {
      await api.put(`/admin/shop/orders/${order.order_id}/archive`, null, { params: { archived: !order.archived } });
      toast.success(order.archived ? "Commande désarchivée" : "Commande archivée");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'archivage");
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement cette commande ? Cette action est irréversible.")) return;
    try {
      await api.delete(`/admin/shop/orders/${order.order_id}`);
      toast.success("Commande supprimée");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la suppression");
    }
  };

  const createShipment = async () => {
    setCreatingShipment(true);
    try {
      await api.post(`/admin/shop/orders/${order.order_id}/create-shipment`);
      toast.success("Expédition Boxtal créée - l'étiquette arrive dans quelques instants");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la création de l'expédition");
    } finally {
      setCreatingShipment(false);
    }
  };

  const openLabel = async () => {
    setFetchingLabel(true);
    try {
      const { data } = await api.get(`/admin/shop/orders/${order.order_id}/label`);
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Étiquette indisponible");
    } finally {
      setFetchingLabel(false);
    }
  };

  const refund = async () => {
    if (!window.confirm("Rembourser cette commande via Stripe et la passer en \"annulée\" ? Cette action est irréversible.")) return;
    setRefunding(true);
    try {
      await api.post(`/admin/shop/orders/${order.order_id}/refund`);
      toast.success("Commande remboursée");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du remboursement");
    } finally {
      setRefunding(false);
    }
  };

  return (
    <div data-theme="dark" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-glass w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={20} /></button>
        <h2 className="font-display font-bold text-xl mb-1">Commande</h2>
        <p className="text-[10px] text-white/30 mb-4">{order.order_id}</p>

        <div className="space-y-1 mb-4">
          {(order.items || []).map((it, i) => (
            <p key={i} className="text-sm">{it.product_name}{it.size && ` (${it.size})`} × {it.quantity}</p>
          ))}
        </div>
        <div className="flex justify-between items-center py-3 border-t border-b border-white/5 mb-4">
          <span className="text-xs text-white/40">Total payé</span>
          <span className="font-display font-bold text-[#39FF14]">{((order.amount_total_cents || 0) / 100).toFixed(2)}€</span>
        </div>

        <div className="space-y-1 text-sm mb-4">
          <p><span className="text-white/40">Client·e :</span> {order.customer_name || "—"}</p>
          <p><span className="text-white/40">Email :</span> <a href={`mailto:${order.customer_email}`} className="text-[#00F0FF] hover:underline">{order.customer_email}</a></p>
          <p><span className="text-white/40">Mode :</span> {order.fulfillment_method === "pickup" ? "Retrait au siège" : "Livraison à domicile"}</p>
          {order.shipping_address && (
            <p><span className="text-white/40">Adresse :</span> {[order.shipping_address.line1, order.shipping_address.postal_code, order.shipping_address.city].filter(Boolean).join(", ")}</p>
          )}
          <p><span className="text-white/40">Date :</span> {new Date(order.created_at).toLocaleString("fr-FR")}</p>
        </div>

        {order.fulfillment_method === "delivery" && (
          <div className="mb-4 p-3 rounded-xl bg-white/5 space-y-2">
            <p className="text-[10px] uppercase tracking-widest text-white/40 font-display">Étiquette Boxtal</p>
            {!order.boxtal_shipping_order_id ? (
              <button onClick={createShipment} disabled={creatingShipment} className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#00F0FF]/10 text-[#00F0FF] text-xs hover:bg-[#00F0FF]/20 disabled:opacity-50">
                <Truck size={14} /> {creatingShipment ? "Création..." : "Générer l'expédition"}
              </button>
            ) : (
              <>
                <p className="text-xs text-white/50">Expédition Boxtal : {order.boxtal_shipping_order_id} {order.boxtal_status ? `(${order.boxtal_status})` : ""}</p>
                <button onClick={openLabel} disabled={fetchingLabel} className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-white/5 text-white/70 text-xs hover:bg-white/10 disabled:opacity-50">
                  <FileText size={14} /> {fetchingLabel ? "..." : "Ouvrir l'étiquette (PDF)"}
                </button>
              </>
            )}
          </div>
        )}

        <div className="space-y-3 mb-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40 font-display mb-1 block">Statut</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-[#14141A] border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-[#00F0FF]">
              {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {status === "shipped" && (
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Transporteur (Colissimo...)" value={carrier} onChange={(e) => setCarrier(e.target.value)} className="w-full bg-[#14141A] border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00F0FF]" />
              <input placeholder="Numéro de suivi" value={tracking} onChange={(e) => setTracking(e.target.value)} className="w-full bg-[#14141A] border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00F0FF]" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40 font-display mb-1 block">Notes internes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Visibles uniquement par le Bureau" className="w-full bg-[#14141A] border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00F0FF]" />
          </div>
        </div>

        <button onClick={save} disabled={saving} className="btn-neon w-full !py-3 disabled:opacity-50 mb-3">
          {saving ? "..." : "Enregistrer"}
        </button>

        <div className="flex gap-2">
          <button onClick={toggleArchive} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-white/5 text-white/70 text-xs hover:bg-white/10">
            {order.archived ? <><ArchiveRestore size={14} /> Désarchiver</> : <><Archive size={14} /> Archiver</>}
          </button>
          {order.status !== "cancelled" && (
            <button onClick={refund} disabled={refunding} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#FFD700]/10 text-[#FFD700] text-xs hover:bg-[#FFD700]/20 disabled:opacity-50">
              <RotateCcw size={14} /> {refunding ? "..." : "Rembourser"}
            </button>
          )}
          <button onClick={remove} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#FF0033]/10 text-[#FF0033] text-xs hover:bg-[#FF0033]/20">
            <Trash2 size={14} /> Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}
